/**
 * Bloco de Listagem Comparativa (genérico — ex.: comparar seguradoras).
 * Contrato de tipos + helpers compartilhados entre o Component (server) e o Client.
 * Ver `docs/editor-e-blocos/BLOCO_LISTAGEM_COMPARATIVA_CONFIGURAVEL_PLANO_2026_06_24.md`.
 *
 * Rotulagem AGNÓSTICA (§0 do plano): nada de domínio cravado — `item`/`listagem`.
 */

export type ListingType = 'custom'

/** Código de região (ISO2 minúsculo, ex.: 'br', 'pt') ou 'int' (internacional/global). */
export type ListingRegionCode = string

/** Métrica variável de um item (ex.: franquia, cobertura, assistência 24h). */
export type ListingAttribute = {
  key: string
  label: string
  value: string
  note?: string | null
  /** Valor numérico p/ ordenação CONFIÁVEL (ex.: 0.1, 35, 3200). Sem ele, cai no parser de texto. */
  numericValue?: number | null
}

/** Item resolvido pelo server e passado (serializável) ao Client. */
export type ListingItem = {
  id: string
  slug: string
  name: string
  type: ListingType
  regions: ListingRegionCode[]
  logoSrc: string
  logoAlt: string
  logoWidth: number
  logoHeight: number
  /** Cor de fundo custom do container do logo (hex ou `transparent`); null = fundo padrão. */
  logoBackground: string | null
  score: number | null
  scoreMax: number
  verified: boolean
  /** URL interna da revisão (relationship→pages/posts resolvido) ou texto livre. */
  reviewHref: string | null
  reviewLabel: string | null
  /** Link de afiliado (recebe rel=sponsored). */
  ctaHref: string
  /** Override por item; o bloco unifica por padrão. */
  ctaLabel: string | null
  attributes: ListingAttribute[]
  /** Conteúdo editorial do painel (texto plano por parágrafos). */
  expertTake: string | null
  pros: string[]
  cons: string[]
  badge: string | null
  /** ISO p/ JSON-LD datePublished/dateModified. */
  publishedAt: string | null
  updatedAt: string | null
}

/**
 * URL da bandeira circular via flagcdn (mesmo CDN do módulo fiatFlags; servida por <img>, sem
 * next/image). O código ISO2 já É o slug do flagcdn; 'int' usa a bandeira da ONU (`un`).
 * Os NOMES das regiões são resolvidos no locale da request pelo server (ver `regionLabelMap` em
 * ComparisonListingClientProps); o catálogo estático `countries.ts` é só p/ as opções do Admin.
 */
export function regionFlagSrc(code: string, size: 'w40' | 'w80' = 'w40'): string {
  const iso = code === 'int' ? 'un' : code.toLowerCase()
  return `https://flagcdn.com/${size}/${iso}.png`
}

/** Colunas "core" (render fixo) + 'attribute' (liga a um attributes[].key). */
export type ColumnKind = 'name' | 'region' | 'attribute' | 'score' | 'actions'

export type ColumnConfig = {
  kind: ColumnKind
  attributeKey?: string | null
  visible: boolean
  label?: string | null
  tooltip?: string | null
  align: 'left' | 'center' | 'right'
  sortable: boolean
  widthPct?: number | null
}

/** Larguras default por tipo de coluna (% — usadas se widthPct não for setado). */
export const DEFAULT_COLUMN_WIDTH: Record<ColumnKind, number> = {
  name: 14,
  region: 6,
  attribute: 10,
  score: 10,
  actions: 13,
}

// ── Filtro de região ───────────────────────────────────────────────────────
export function filterByRegion(items: ListingItem[], regionId: string): ListingItem[] {
  if (regionId === 'all') return items
  return items.filter((it) => it.regions.includes(regionId))
}

// ── Ordenação (funcional, usa numericValue dos atributos) ────────────────────
/** Parser de texto p/ fallback de ordenação (quando o atributo não tem numericValue). */
function parseNumeric(value: string): number {
  const cleaned = value.replace(/[^\d.,]/g, '')
  if (!cleaned) return Number.POSITIVE_INFINITY
  // PT: '.'=milhar, ','=decimal.
  const n = parseFloat(cleaned.replace(/\./g, '').replace(',', '.'))
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY
}

function attributeNumeric(item: ListingItem, key: string): number {
  const attr = item.attributes.find((a) => a.key === key)
  if (!attr) return Number.POSITIVE_INFINITY
  if (typeof attr.numericValue === 'number' && Number.isFinite(attr.numericValue)) return attr.numericValue
  return parseNumeric(attr.value)
}

/** Uma opção de ordenação derivada de uma coluna sortable (ou 'default'/'name'/'score'). */
export type SortOption = { id: string; label: string; columnKind: ColumnKind; attributeKey?: string | null; dir: 'asc' | 'desc' }

export function sortItems(items: ListingItem[], option: SortOption | undefined): ListingItem[] {
  if (!option || option.id === 'default') return items
  const copy = [...items]
  copy.sort((a, b) => {
    let av: number, bv: number
    if (option.columnKind === 'score') {
      av = a.score ?? -Infinity
      bv = b.score ?? -Infinity
    } else if (option.columnKind === 'name') {
      return option.dir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
    } else if (option.columnKind === 'attribute' && option.attributeKey) {
      av = attributeNumeric(a, option.attributeKey)
      bv = attributeNumeric(b, option.attributeKey)
    } else {
      return 0
    }
    return option.dir === 'asc' ? av - bv : bv - av
  })
  return copy
}

// ── JSON-LD (§7.2): @type da entidade revisada por tipo de listagem ──────────
export function resolveOperatorSchemaType(_type: ListingType): string | string[] {
  return 'Organization'
}

/** Nota com 1 casa decimal, no locale da request (pt "4,8" · en "4.8"). */
export function formatScore(score: number, locale: string = 'pt-BR'): string {
  return score.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

/** Tradutor simples (key→string). O Component passa o `t` do next-intl (namespace `ComparisonListing`). */
export type Translator = (key: string) => string

// ── Contrato server→client (o Component resolve; o Client consome) ───────────

/** Coluna já resolvida (rótulo final, largura, etc.) passada ao Client. */
export type ResolvedColumn = {
  id: string
  kind: ColumnKind
  attributeKey: string | null
  label: string
  tooltip: string | null
  align: 'left' | 'center' | 'right'
  sortable: boolean
  /** classe Tailwind de largura, ex.: 'w-[12%]'. */
  widthClass: string
}

/** Todos os rótulos/textos que o Client precisa (já no locale resolvido). */
export type ListingLabels = {
  entitySingular: string
  entityPlural: string
  ctaLabel: string
  reviewLinkLabel: string
  regionFilterLabel: string
  allRegionsLabel: string
  resultCountTemplate: string
  sortLabel: string
  sortDefaultLabel: string
  showMoreLabel: string
  showLessLabel: string
  expertTakeHeading: string
  prosHeading: string
  consHeading: string
  verifiedLabel: string
  verifiedTooltip: string
  emptyStateText: string
}

/** Flags/enums de comportamento + aparência. */
export type ListingOptions = {
  showRanking: boolean
  highlightTopRank: boolean
  showVerifiedBadge: boolean
  enableRegionFilter: boolean
  enableSort: boolean
  enableDetailsPanel: boolean
  density: 'compact' | 'comfortable'
  mobileLayout: 'cards' | 'table'
  tableMinWidthRem: number
  ctaColor: 'primary' | 'success'
  ctaOpenNewTab: boolean
  /** rel já montado, ex.: 'sponsored nofollow noopener' ou '' (none). */
  ctaRel: string
  anchorId: string | null
  scoreMax: number
}

/** Props EXATAS do `<ComparisonListingClient/>` (contrato fixo server↔client). */
export type ComparisonListingClientProps = {
  items: ListingItem[]
  columns: ResolvedColumn[]
  labels: ListingLabels
  options: ListingOptions
  /**
   * Nomes de região (code→rótulo) JÁ resolvidos no locale da request pelo server (i18n: /en /es
   * mostram o nome traduzido). Só os códigos presentes nos itens. Inclui 'int'. O Client consome
   * direto (não importa o catálogo estático → fora do bundle client).
   */
  regionLabelMap: Record<string, string>
}

/** Rótulo default de coluna core, no locale da request (i18n — não PT hardcoded). */
function columnDefaultLabel(kind: ColumnKind, t: Translator): string {
  if (kind === 'name') return t('colOperator')
  if (kind === 'region') return t('colRegion')
  if (kind === 'score') return t('colScore')
  return '' // attribute (usa attrLabelByKey) / actions (sem header)
}

/** Resolve `rel` do CTA afiliado (SEO-04). */
export function ctaRelString(rel: string): string {
  if (rel === 'sponsored_nofollow') return 'sponsored nofollow noopener'
  if (rel === 'nofollow') return 'nofollow noopener'
  return ''
}

/** Largura Tailwind de uma coluna: widthPct explícito OU default por tipo. */
function columnWidthClass(kind: ColumnKind, widthPct?: number | null): string {
  const pct = typeof widthPct === 'number' && widthPct >= 4 ? widthPct : DEFAULT_COLUMN_WIDTH[kind]
  return `w-[${pct}%]`
}

/**
 * Resolve as colunas do config do bloco → ResolvedColumn[] (só visíveis, rótulo final, largura).
 * `attrLabelByKey` = mapa key→label vindo dos atributos dos itens (default do rótulo de coluna 'attribute').
 */
export function resolveColumns(
  columns: ColumnConfig[] | null | undefined,
  attrLabelByKey: Record<string, string>,
  t: Translator,
): ResolvedColumn[] {
  return (columns ?? [])
    .filter((c) => c.visible !== false)
    .map((c, i) => {
      const key = c.kind === 'attribute' ? (c.attributeKey ?? '') : null
      const fallback =
        c.kind === 'attribute' ? (attrLabelByKey[key ?? ''] ?? key ?? '') : columnDefaultLabel(c.kind, t)
      return {
        id: `${c.kind}-${key ?? i}`,
        kind: c.kind,
        attributeKey: key,
        label: c.label?.trim() || fallback,
        tooltip: c.tooltip?.trim() || null,
        align: c.align ?? 'left',
        sortable: Boolean(c.sortable),
        widthClass: columnWidthClass(c.kind, c.widthPct),
      }
    })
}

/** Opções de ordenação (UI) derivadas das colunas sortable + 'default'. */
export function buildSortOptions(columns: ResolvedColumn[], defaultLabel: string): SortOption[] {
  const opts: SortOption[] = [{ id: 'default', label: defaultLabel, columnKind: 'name', dir: 'desc' }]
  for (const c of columns) {
    if (!c.sortable) continue
    if (c.kind === 'score') opts.push({ id: 'score', label: c.label, columnKind: 'score', dir: 'desc' })
    else if (c.kind === 'name') opts.push({ id: 'name', label: c.label, columnKind: 'name', dir: 'asc' })
    else if (c.kind === 'attribute' && c.attributeKey)
      opts.push({ id: `attr:${c.attributeKey}`, label: c.label, columnKind: 'attribute', attributeKey: c.attributeKey, dir: 'asc' })
  }
  return opts
}

/** Valor + nota de um item para uma coluna de atributo (ou '' se ausente). */
export function attributeCell(item: ListingItem, key: string): { value: string; note: string | null } {
  const a = item.attributes.find((x) => x.key === key)
  return { value: a?.value ?? '—', note: a?.note ?? null }
}

// ── Templates de coluna por preset (usados pelo server quando o bloco não tem colunas) ──
// Rótulos/tooltips guardam CHAVES i18n (namespace ComparisonListing), resolvidas por locale em
// `presetColumns(preset, t)` — antes ficavam em PT hardcoded no demoData (G1 da auditoria i18n).
type PresetColDef = {
  kind: ColumnKind
  attributeKey?: string
  labelKey?: string
  tooltipKey?: string
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
}

const PRESET_COL_DEFS: Record<ListingType, PresetColDef[]> = {
  custom: [
    { kind: 'name' },
    { kind: 'region', align: 'center' },
    { kind: 'score', sortable: true },
    { kind: 'actions', align: 'center' },
  ],
}

/** Colunas-padrão do preset, com rótulos JÁ no locale via `t`. */
export function presetColumns(preset: string | null | undefined, t: Translator): ColumnConfig[] {
  const defs = PRESET_COL_DEFS[(preset as ListingType) ?? 'custom'] ?? PRESET_COL_DEFS.custom
  return defs.map((d) => ({
    kind: d.kind,
    attributeKey: d.attributeKey ?? null,
    visible: true,
    label: d.labelKey ? t(d.labelKey) : null,
    tooltip: d.tooltipKey ? t(d.tooltipKey) : null,
    align: d.align ?? 'left',
    sortable: d.sortable ?? false,
    widthPct: null,
  }))
}
